<template>
    <label
        class="accessible-el-select text-sm font-semibold text-secondary-600 block mb-1"
    >
        <slot name="label" />
        <el-form-item :prop="prop" v-if="withFormItem">
            <el-select
                :disabled="isDisabled"
                :multiple="isMultiple"
                :collapse-tags="collapseTags"
                ref="select"
                class="w-full"
                v-model="valueProxy"
                :clearable="clearable"
                :filterable="filterable"
                :allow-create="allowCreate"
                :maxlength="maxlength"
                :placeholder="placeholder"
                :loading="loading"
                :remote="remote"
                :remote-method="remoteMethod" 
                :reserve-keyword="reserveKeyword"
                :size="size"
                :valueKey="valueKey"
                @focus="handleFocus"
                @blur="handleBlur"
                @visible-change="handleDropdownVisible"
                @change="onChange"
                v-on="$listeners"
            >
                <template v-if="group">
                  <el-option-group
                    v-for="optionGroup in normalizedOptions"
                    :key="optionGroup.label"
                    :label="optionGroup.label"
                  >
                    <el-option
                      v-for="option in optionGroup.options"
                      :key="option.id"
                      :label="option.name"
                      :value="option.value"
                      :disabled="option.disabled"
                      @select="onSelect"
                    />
                  </el-option-group>
                </template>
                <template v-else>
                    <el-option 
                        v-for="option in normalizedOptions"
                        :key="option.id"
                        :label="option.name"
                        :value="option.value"
                        :disabled="option.disabled"
                        @select="onSelect"
                    />
                </template>
            </el-select>
        </el-form-item>
        <el-select v-else
            :disabled="isDisabled"
            :multiple="isMultiple"
            :collapse-tags="collapseTags"
            ref="select"
            class="w-full"
            v-model="valueProxy"
            :clearable="clearable"
            :filterable="filterable"
            :allow-create="allowCreate"
            :maxlength="maxlength"
            :placeholder="placeholder"
            :loading="loading"
            :remote="remote"
            :remote-method="remoteMethod" 
            :reserve-keyword="reserveKeyword"
            :size="size"
            :valueKey="valueKey"
            @focus="handleFocus"
            @blur="handleBlur"
            @visible-change="handleDropdownVisible"
            @change="onChange"
            v-on="$listeners"
        >
            <template v-if="group">
                <el-option-group
                    v-for="optionGroup in normalizedOptions"
                    :key="optionGroup.label"
                    :label="optionGroup.label"
                >
                    <el-option
                        v-for="option in optionGroup.options"
                        :key="option.id"
                        :label="option.name"
                        :value="option.value"
                        :disabled="option.disabled"
                        @select="onSelect"
                    />
                </el-option-group>
            </template>
            <template v-else>
                <el-option
                    v-for="option in normalizedOptions"
                    :key="option.id"
                    :label="option.name"
                    :value="option.value"
                    :disabled="option.disabled"
                    @select="onSelect"
                />
            </template>
        </el-select>
    </label>
</template>

<script>
import { dropdownState } from '@/state/dropdownState'

export default {
  name: 'AccessibleSelect',
  props: {
    value: [String, Number, Object, null],
    prop: { 
      type: String,
      required: true 
    },
    options: { // [{ id, value, name }] or [{ label, options: [{ id, value, name }] }] when group=true
      type: Array, 
      required: true 
    },
    group: {
      type: Boolean,
      default: false
    }, 
    idPrefix: { 
      type: String, 
      default: 'select' 
    },
    withFormItem: {
      type: Boolean,
      default: true,
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    isMultiple: {
      type: Boolean,
      default: false,
    },
    collapseTags: {
      type: Boolean,
      default: false,
    },
    allowCreate: {
      type: Boolean,
      default: false,
    },
    maxlength: {
      type: String,
      default: null,
    },
    placeholder: {
      type: String,
      default: '',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    remote: {
      type: Boolean,
      default: false,
    },
    remoteMethod: {
      type: Function,
      default: null,
    },
    reserveKeyword: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: null,
    },
    valueKey: {
      type: String,
      default: null,
    }
  },
  data () {
    return {
      // Para selects remotos necesitamos que filterable esté activo desde el inicio
      filterable: this.remote,
      observer: null,
      pasteListener: null
    }
  },
  mounted () {
    this.pasteListener = this.onInputPaste.bind(this)
    this.clearKeydownListener = this.onClearKeydown.bind(this)
    this.focusinListener = this.onSelectFocusin.bind(this)
    this.focusoutListener = this.onSelectFocusout.bind(this)
    this.applyComboboxAria()
    this.attachPasteListener()
    this.attachFocusListeners()
    this.enhanceClearButton()
  },
  computed: {
    valueProxy: {
      get () {
        return this.value
      },
      set (val) {
        this.$emit('input', val)
      }
    },
    normalizedOptions () {
      if (this.group) {
        // Para opciones agrupadas: [{ label, options: [{ id, value, name }] }]
        return this.options?.map(optionGroup => ({
          ...optionGroup,
          options: optionGroup.options.map(option => ({
            ...option,
            value: option.value !== undefined ? option.value : option.id
          }))
        }))
      } else {
        // Para opciones simples: [{ id, value, name }]
        return this.options?.map(option => ({
          ...option,
          value: option.value !== undefined ? option.value : option.id
        }))
      }
    }
  },
  methods: {
    getSelectRoot () {
      return this.$refs.select?.$el || null
    },
    getSelectInputs () {
      const root = this.getSelectRoot()
      if (!root) return []

      // Element UI select uses different inputs:
      // - .el-input__inner: readonly display input (single mode)
      // - .el-select__input: real editable filter input (filterable / remote / multiple)
      const filterInput = root.querySelector('input.el-select__input')
      const displayInput = root.querySelector('input.el-input__inner')

      return [filterInput, displayInput].filter(Boolean)
    },
    getComboboxInput () {
      const root = this.getSelectRoot()
      if (!root) return null
      return root.querySelector('input.el-select__input') || root.querySelector('input.el-input__inner')
    },
    applyComboboxAria () {
      const input = this.getComboboxInput()
      if (!input) return
      input.setAttribute('role', 'combobox')
      input.setAttribute('aria-haspopup', 'listbox')
      input.setAttribute('aria-expanded', 'false')
    },
    attachPasteListener () {
      if (!this.remote || !this.remoteMethod) return
      const inputs = this.getSelectInputs()
      inputs.forEach(input => {
        if (!input || input.__accessiblePasteListenerAdded) return
        input.addEventListener('paste', this.pasteListener)
        input.__accessiblePasteListenerAdded = true
      })
    },
    attachFocusListeners () {
      const root = this.getSelectRoot()
      if (!root || root.__accessibleFocusListenersAdded) return
      root.addEventListener('focusin', this.focusinListener)
      root.addEventListener('focusout', this.focusoutListener)
      root.__accessibleFocusListenersAdded = true
    },
    onSelectFocusin () {
      // Forzar inputHovering para que Element UI muestre el icono de limpiar con teclado
      const select = this.$refs.select
      if (select) {
        select.inputHovering = true
      }
      this.$nextTick(() => this.enhanceClearButton())
    },
    onSelectFocusout (e) {
      const root = this.getSelectRoot()
      // Solo desactivar si el foco sale completamente del select
      if (root && !root.contains(e.relatedTarget)) {
        const select = this.$refs.select
        if (select) {
          select.inputHovering = false
        }
      }
    },
    enhanceClearButton () {
      const root = this.getSelectRoot()
      if (!root) return
      const clearIcon = root.querySelector('.el-icon-circle-close')
      if (!clearIcon || clearIcon.__accessibleClearEnhanced) return
      clearIcon.setAttribute('tabindex', '0')
      clearIcon.setAttribute('role', 'button')
      clearIcon.setAttribute('aria-label', this.placeholder ? `Borrar ${this.placeholder}` : 'Borrar selección')
      clearIcon.addEventListener('keydown', this.clearKeydownListener)
      clearIcon.__accessibleClearEnhanced = true
    },
    onClearKeydown (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        const select = this.$refs.select
        if (select && typeof select.handleClearClick === 'function') {
          select.handleClearClick(e)
        }
        // Devolver foco al input principal
        this.$nextTick(() => {
          const input = this.getComboboxInput()
          if (input) input.focus()
        })
      }
    },
    onChange (item) {
      // console.log('change', item)
      this.$emit('change', item)
      // Tras seleccionar un valor, Element UI crea el icono de limpiar (v-if).
      // Necesitamos enhancerlo para que sea focusable con teclado.
      this.$nextTick(() => this.enhanceClearButton())
    },
    onSelect (item) {
      // console.log('select', item)
      this.$emit('select', item)
    },
    handleFocus () {
      this.filterable = true
      this.$nextTick(() => {
        this.applyComboboxAria()
        this.attachPasteListener()
        this.enhanceClearButton()
      })
    },
    handleBlur () {
      if (!this.$refs.select || !this.$refs.select.visible) {
        this.filterable = false
      }
    },
    onInputPaste (e) {
      if (!this.remoteMethod) return

      const pastedText = (e.clipboardData || window.clipboardData).getData('text')
      const target = e.target
      const isReadonlyTarget = !!(target && target.readOnly)

      // If the user pasted into the readonly display input, Element UI won't insert the text.
      // In that case, prevent default, open the dropdown and inject the text into the filter input.
      if (isReadonlyTarget) {
        e.preventDefault()
        this.filterable = true

        // Try to open dropdown so the filter input exists.
        const select = this.$refs.select
        if (select && typeof select.toggleMenu === 'function') {
          select.toggleMenu()
        }

        this.$nextTick(() => {
          const root = this.getSelectRoot()
          const filterInput = root?.querySelector('input.el-select__input')
          if (filterInput) {
            filterInput.value = pastedText
            // Trigger Element UI internal input handler
            filterInput.dispatchEvent(new Event('input', { bubbles: true }))
            filterInput.dispatchEvent(new Event('change', { bubbles: true }))
          }
          this.remoteMethod(pastedText)
        })
        return
      }

      // Normal case: allow default paste, then read actual input value.
      this.$nextTick(() => {
        const query = target && typeof target.value === 'string' ? target.value : pastedText
        this.remoteMethod(query)
      })
    },
    handleDropdownVisible (visible) {
      this.$emit('visible-change', visible)
      // Actualiza el estado global para que los Dialogs puedan bloquear close-on-click-modal
      if (visible) {
        dropdownState.isAnyDropdownOpen = true
      } else {
        setTimeout(() => {
          dropdownState.isAnyDropdownOpen = false
        }, 200)
      }
      const input = this.getComboboxInput()

      if (!visible) {
        if (input) {
          input.setAttribute('aria-expanded', 'false')
          input.removeAttribute('aria-activedescendant')
        }
        // En selects remotos mantenemos filterable activo para no romper la búsqueda
        if (!this.remote) {
          this.filterable = false
        }
        if (this.observer) {
          this.observer.disconnect()
          this.observer = null
        }
        return
      }

      this.filterable = true

      this.$nextTick(() => {
        this.applyComboboxAria()
        this.attachPasteListener()

        const dropdowns = Array.from(document.querySelectorAll('.el-select-dropdown'))
        const visibleDropdown = dropdowns.reverse().find(dd =>
          dd.style.display !== 'none' && dd.querySelector('.el-select-dropdown__list')
        )
        if (!visibleDropdown) return

        const list = visibleDropdown.querySelector('.el-select-dropdown__list')
        if (!list) return

        list.setAttribute('role', 'listbox')

        // Handle group headers and options
        const groupHeaders = list.querySelectorAll('.el-select-group__title')
        groupHeaders.forEach((header, index) => {
          header.setAttribute('role', 'group')
          header.setAttribute('id', `${this.prop}-group-${index}`)
        })

        const items = list.querySelectorAll('.el-select-dropdown__item')
        items.forEach((item, index) => {
          item.setAttribute('role', 'option')
          item.setAttribute('id', `${this.prop}-option-${index}`)
          const text = item.innerText.trim()
          const isSelected = text === this.getLabelByValue(this.valueProxy)
          item.setAttribute('aria-selected', isSelected.toString())
        })

        const input = this.getComboboxInput()
        if (!input) return

        input.setAttribute('aria-expanded', 'true')

        const selectedIndex = this.findOptionIndex(this.valueProxy)
        if (selectedIndex >= 0) {
          input.setAttribute('aria-activedescendant', `${this.prop}-option-${selectedIndex}`)
        }

        this.observer = new MutationObserver(() => {
          const active = list.querySelector('.el-select-dropdown__item.hover')
          if (active) {
            input.setAttribute('aria-activedescendant', active.id)
          }
        })
        this.observer.observe(list, {
          subtree: true,
          attributes: true,
          attributeFilter: ['class']
        })
      })
    },
    getLabelByValue (value) {
      if (this.group) {
        for (const optionGroup of this.normalizedOptions) {
          const opt = optionGroup.options.find(o => o.value === value)
          if (opt) return opt.name
        }
        return ''
      } else {
        const opt = this.normalizedOptions.find(o => o.value === value)
        return opt ? opt.name : ''
      }
    },
    findOptionIndex (value) {
      if (this.group) {
        let index = 0
        for (const optionGroup of this.normalizedOptions) {
          for (const option of optionGroup.options) {
            if (option.value === value) {
              return index
            }
            index++
          }
        }
        return -1
      } else {
        return this.normalizedOptions.findIndex(opt => opt.value === value)
      }
    }
  },
  destroyed () {
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }

    const inputs = this.getSelectInputs ? this.getSelectInputs() : []
    inputs.forEach(input => {
      if (input && this.pasteListener) {
        input.removeEventListener('paste', this.pasteListener)
      }
    })

    const root = this.getSelectRoot ? this.getSelectRoot() : null
    if (root) {
      root.removeEventListener('focusin', this.focusinListener)
      root.removeEventListener('focusout', this.focusoutListener)
      const clearIcon = root.querySelector('.el-icon-circle-close')
      if (clearIcon) {
        clearIcon.removeEventListener('keydown', this.clearKeydownListener)
      }
    }
  }
}
</script>

<style scoped>
::v-deep .el-input:focus-within .el-input__suffix .el-icon-circle-close {
  display: inline-block !important;
}
::v-deep .el-input__suffix-inner .el-icon-circle-close {
  color: var(--secondary-400) !important; 
  padding-left: 0.25rem;
  padding-right: 0.25rem;
}
::v-deep .el-input__suffix-inner .el-icon-circle-close:hover {
  color: var(--secondary-800) !important;
}
::v-deep .el-input__suffix-inner .el-icon-circle-close:focus {
  color: var(--secondary-800) !important; 
  outline: 2px solid var(--secondary-800) !important;
}
</style>
